import type { DbUser } from "@prisma/client";
import {
  BookOpen,
  BookOpenCheck,
  Check,
  CreditCard,
  GitCommit,
  Github,
  LogOut,
  type LucideIcon,
  Map,
  Menu,
} from "lucide-react";
import type { ReactNode } from "react";
import { Form, Link, NavLink, useLocation } from "react-router";

import { ThemeControl } from "@/theme/theme-control";
import { Button } from "@/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/dropdown-menu";
import { ProfileAvatar } from "@/utils/avatar";
import { cn } from "@/utils/tailwind";

interface CourseShellProps {
  children: ReactNode;
  dbUser: DbUser | null;
}

type PrimarySection = "commits" | "concepts" | "course";

interface PrimaryNavItem {
  icon: LucideIcon;
  label: string;
  section: PrimarySection;
  to: string;
}

const primaryNavItems = [
  {
    icon: BookOpen,
    label: "Course",
    section: "course",
    to: "/course",
  },
  {
    icon: BookOpenCheck,
    label: "Concepts",
    section: "concepts",
    to: "/concepts",
  },
  {
    icon: GitCommit,
    label: "Repository",
    section: "commits",
    to: "/commits",
  },
] as const satisfies readonly PrimaryNavItem[];

const repositoryUrl = "https://github.com/quietcolin/makefile-fyi";

export function CourseShell({ children, dbUser }: CourseShellProps) {
  const { pathname } = useLocation();
  const activeSection = getActiveSection(pathname);

  return (
    <div className="bg-background text-foreground min-h-dvh">
      <header className="bg-background/90 supports-[backdrop-filter]:bg-background/75 sticky top-0 z-40 border-b backdrop-blur">
        <div className="mx-auto flex h-14 w-[calc(100%-2rem)] max-w-7xl items-center gap-3 sm:w-[calc(100%-3rem)]">
          <Link
            className="focus-visible:ring-ring/50 shrink-0 rounded-sm font-mono text-sm font-medium underline-offset-4 outline-none hover:underline focus-visible:ring-2"
            to="/"
          >
            makefile.fyi
          </Link>

          <DesktopPrimaryNavigation />

          <div className="ml-auto flex min-w-0 items-center gap-1 sm:gap-1.5">
            <DesktopUtilityNavigation pathname={pathname} />
            <MobileNavigation
              activeSection={activeSection}
              pathname={pathname}
            />
            {dbUser ? (
              <CourseAccountControl dbUser={dbUser} />
            ) : (
              <CourseLoginControl />
            )}
            <ThemeControl />
          </div>
        </div>
      </header>

      {children}
    </div>
  );
}

function DesktopPrimaryNavigation() {
  return (
    <nav aria-label="Primary" className="ml-3 hidden h-full lg:flex">
      {primaryNavItems.map((item) => (
        <NavLink
          key={item.section}
          to={item.to}
          className={({ isActive }) =>
            cn(
              "text-muted-foreground hover:text-foreground flex h-full items-center border-b-2 border-transparent px-3 text-sm font-normal transition",
              isActive && "border-foreground text-foreground font-medium",
            )
          }
        >
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function DesktopUtilityNavigation({ pathname }: { pathname: string }) {
  return (
    <nav aria-label="Utilities" className="hidden h-full items-center lg:flex">
      <Link
        aria-current={pathname === "/guide" ? "page" : undefined}
        to="/guide"
        className={cn(
          "text-muted-foreground hover:text-foreground flex h-full items-center px-2.5 text-sm font-normal transition",
          pathname === "/guide" && "text-foreground",
        )}
      >
        How it works
      </Link>
      <Link
        aria-current={pathname === "/pricing" ? "page" : undefined}
        to="/pricing"
        className={cn(
          "text-muted-foreground hover:text-foreground flex h-full items-center px-2.5 text-sm font-normal transition",
          pathname === "/pricing" && "text-foreground",
        )}
      >
        Pricing
      </Link>
      <a
        className="text-muted-foreground hover:text-foreground flex h-full items-center px-2.5 text-sm font-normal transition"
        href={repositoryUrl}
        rel="noreferrer"
        target="_blank"
      >
        GitHub
      </a>
    </nav>
  );
}

interface MobileNavigationProps {
  activeSection: PrimarySection | null;
  pathname: string;
}

function MobileNavigation({ activeSection, pathname }: MobileNavigationProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label="Open navigation"
          className="lg:hidden"
          size="icon"
          type="button"
          variant="ghost"
        >
          <Menu />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" sideOffset={8}>
        <DropdownMenuLabel>Navigate</DropdownMenuLabel>
        <DropdownMenuGroup>
          {primaryNavItems.map((item) => (
            <MobilePrimaryNavigationItem
              key={item.section}
              activeSection={activeSection}
              item={item}
            />
          ))}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link
              aria-current={pathname === "/guide" ? "page" : undefined}
              to="/guide"
            >
              <Map />
              How it works
              {pathname === "/guide" && <Check className="ml-auto" />}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              aria-current={pathname === "/pricing" ? "page" : undefined}
              to="/pricing"
            >
              <CreditCard />
              Pricing
              {pathname === "/pricing" && <Check className="ml-auto" />}
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <a href={repositoryUrl} rel="noreferrer" target="_blank">
              <Github />
              GitHub
            </a>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface MobilePrimaryNavigationItemProps {
  activeSection: PrimarySection | null;
  item: PrimaryNavItem;
}

function MobilePrimaryNavigationItem({
  activeSection,
  item,
}: MobilePrimaryNavigationItemProps) {
  const Icon = item.icon;
  const isActive = activeSection === item.section;

  return (
    <DropdownMenuItem asChild>
      <Link aria-current={isActive ? "page" : undefined} to={item.to}>
        <Icon />
        {item.label}
        {isActive && <Check className="ml-auto" />}
      </Link>
    </DropdownMenuItem>
  );
}

function CourseLoginControl() {
  return (
    <Button asChild size="sm" variant="outline">
      <Link to="/login">Login</Link>
    </Button>
  );
}

function CourseAccountControl({ dbUser }: { dbUser: DbUser }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          aria-label={`Open account menu for ${dbUser.email}`}
          className="size-8 rounded-full p-0 has-[>svg]:px-0"
          size="icon"
          type="button"
          variant="outline"
        >
          <ProfileAvatar className="size-6" dbUser={dbUser} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" sideOffset={8}>
        <DropdownMenuLabel className="grid gap-1">
          <span>Account</span>
          <span className="text-muted-foreground truncate text-xs font-normal">
            {dbUser.email}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/billing">
              <CreditCard />
              Billing
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <Form action="/logout" method="POST">
          <DropdownMenuItem asChild variant="destructive">
            <button className="w-full" type="submit">
              <LogOut />
              Log out
            </button>
          </DropdownMenuItem>
        </Form>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function getActiveSection(pathname: string): PrimarySection | null {
  if (pathname === "/commits" || pathname.startsWith("/commits/")) {
    return "commits";
  }

  if (pathname === "/concepts" || pathname.startsWith("/concepts/")) {
    return "concepts";
  }

  if (pathname === "/course" || pathname.startsWith("/course/")) {
    return "course";
  }

  return null;
}
